import { LogOut, UserRoundCog } from 'lucide-react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { dataSource } from '@/data';
import { definirPersonificacao } from '@/data/personificacao';
import { usePersonificacao } from '@/hooks/use-personificacao';

/**
 * A cada quanto o heartbeat renova a trava de personificação (ver
 * `EstadoInstalacaoService.adquirir_sessao_personificacao` no backend, TTL
 * de 90s) — bem abaixo do TTL, pra um heartbeat perdido isolado não derrubar
 * a sessão.
 */
const HEARTBEAT_MS = 30_000;

/**
 * Barra de personificação — fica ACIMA da topbar (não dentro dela), pra nunca
 * se confundir com um elemento normal da navegação: enquanto está ali, toda
 * ação da pessoa vale para a instalação personificada, não para as dela.
 *
 * Sair recarrega a página de propósito — é a forma mais simples de garantir
 * que as sete telas de leitura (cada uma com seu próprio `useResource`)
 * refaçam a busca sob o escopo correto, sem coordenar um recarregamento
 * manual em cada uma.
 *
 * Também é responsável por manter viva a trava contra personificação
 * simultânea da mesma instalação: heartbeat periódico enquanto a barra está
 * montada, liberação ao clicar Sair e ao fechar a aba (`pagehide`). Sem
 * nenhum desses três a trava ainda cai sozinha pelo TTL — são só o jeito de
 * liberar mais rápido pros outros do que esperar o tempo todo.
 */
export function PersonificacaoBanner() {
  const personificacao = usePersonificacao();
  const instalacaoInfraId = personificacao?.instalacaoInfraId ?? null;

  // Heartbeat: mantém a trava viva enquanto a barra está montada (ou seja,
  // enquanto há uma personificação ativa nesta aba).
  useEffect(() => {
    if (instalacaoInfraId == null) return;

    let cancelado = false;

    async function renovar() {
      try {
        await dataSource.abrirOuRenovarSessaoPersonificacao(instalacaoInfraId!);
      } catch {
        // 409 (outra sessão tomou a trava porque este heartbeat atrasou
        // demais — laptop suspenso, aba em background por muito tempo) ou
        // falha de rede transitória: nos dois casos, mais seguro empurrar a
        // pessoa pra fora da personificação do que deixar continuar achando
        // que ainda está protegida contra uso simultâneo.
        if (cancelado) return;
        toast.error('Sua sessão de personificação expirou ou foi assumida por outra pessoa.');
        definirPersonificacao(null);
        window.location.reload();
      }
    }

    const id = window.setInterval(renovar, HEARTBEAT_MS);
    return () => {
      cancelado = true;
      window.clearInterval(id);
    };
  }, [instalacaoInfraId]);

  // F5/fechar aba destroem o JS antes de qualquer cleanup rodar — `pagehide`
  // (não `beforeunload`) + `keepalive` é o mesmo padrão de
  // `liberarRetencaoAtual`/`aoSairDaPagina` em `AgendarPage`.
  useEffect(() => {
    if (instalacaoInfraId == null) return;
    function aoSairDaPagina() {
      void dataSource
        .encerrarSessaoPersonificacao(instalacaoInfraId!, { melhorEsforco: true })
        .catch(() => undefined);
    }
    window.addEventListener('pagehide', aoSairDaPagina);
    return () => window.removeEventListener('pagehide', aoSairDaPagina);
  }, [instalacaoInfraId]);

  if (!personificacao) return null;

  function sair() {
    if (instalacaoInfraId != null) {
      void dataSource.encerrarSessaoPersonificacao(instalacaoInfraId).catch(() => undefined);
    }
    definirPersonificacao(null);
    window.location.reload();
  }

  return (
    <div className="flex h-9 shrink-0 items-center justify-center gap-3 border-b border-warning-border bg-warning-wash px-3 text-center">
      <UserRoundCog className="size-4 shrink-0 text-warning-text" aria-hidden="true" />
      <span className="truncate text-label font-bold text-warning-text">
        Você está personificando a instalação <strong>{personificacao.nome}</strong>. Para retornar, clique em Sair.
      </span>
      <Button variant="outline" size="sm" onClick={sair} className="shrink-0">
        <LogOut className="size-3.5" aria-hidden="true" />
        Sair
      </Button>
    </div>
  );
}
